import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LoadingBlock } from './components/ui';
import { useAuth } from './lib/auth';

import { LoginPage } from './pages/Login';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordReset';
import { ChangePasswordPage } from './pages/ChangePassword';
import { DashboardPage } from './pages/Dashboard';
import { ClientsPage, ClientDetailPage } from './pages/Clients';
import { ClientFormPage, ClientContactFormPage } from './pages/ClientForm';
import { ProjectMoneyPage } from './pages/ProjectMoney';
import {
  InvoiceFormPage,
  PaymentFormPage,
  InvoiceDiscountPage,
  InvoiceCancelPage,
  PaymentReversePage,
} from './pages/InvoiceForm';
import {
  RestoreInvoicePage,
  RestorePaymentPage,
  DiscardMigrationRowPage,
} from './pages/MigrationForm';
import { BudgetFormPage } from './pages/BudgetForm';
import { ExpenseFormPage, ExpenseCategoryFormPage } from './pages/ExpenseForm';
import { PayrollPeriodFormPage, PayrollAdjustmentFormPage } from './pages/PayrollForm';
import { PayrollPage, PayrollPeriodPage } from './pages/Payroll';
import { PointsRewardsPage } from './pages/PointsRewards';
import { PointsRewardCancelPage, PointsRewardPaidPage } from './pages/PointsRewardForm';
import { SalariesPage, SalaryHistoryPage } from './pages/Salaries';
import { SalaryFormPage } from './pages/SalaryForm';
import { ExpensesPage } from './pages/Expenses';
import { InvoicesPage, InvoiceDetailPage } from './pages/Invoices';
import { PaymentsPage } from './pages/Payments';
import { ContractsPage, ContractDetailPage } from './pages/Contracts';
import { ContractFormPage, ContractTerminatePage } from './pages/ContractForm';
import { SubscriptionsPage, SubscriptionDetailPage } from './pages/Subscriptions';
import { SubscriptionFormPage, SubscriptionTermsPage, SubscriptionCancelPage } from './pages/SubscriptionForm';
import { SubscriptionsOverviewPage, SubscriptionBillingPage } from './pages/SubscriptionOverview';
import { PackagesPage, OfferProjectPage, PackageDetailPage, PackageFormPage, PackageArchivePage } from './pages/SubscriptionPackages';
import {
  ExternalSystemDetailPage,
  ExternalSystemFormPage,
  SystemSuspendPage,
  TokenIssuePage,
  TokenRevokePage,
  TokenRotatePage,
  WebhookSecretPage,
} from './pages/ExternalSystems';
import { BudgetsPage } from './pages/Budgets';
import { DataMigrationPage } from './pages/DataMigration';
import { ReportsPage } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { AuditLogPage } from './pages/AuditLog';
import { AccessPage } from './pages/Access';
import { AccessFormPage, AccessRevokePage } from './pages/AccessForm';
import { NotFoundPage } from './pages/NotFound';
import { PartnersPage, PartnerDetailPage, PartnerTransactionsPage, PartnerTransactionPage } from './pages/Partners';
import {
  PartnerFormPage,
  PartnerShareFormPage,
  PartnerTransactionFormPage,
  PartnerTransactionReversePage,
} from './pages/PartnerForm';
import { HistoricalFinancePage, HistoricalRecordPage } from './pages/HistoricalFinance';
import {
  HistoricalRecordFormPage,
  HistoricalFinalizePage,
  HistoricalCorrectPage,
  HistoricalVoidPage,
} from './pages/HistoricalForm';
import { FinancialSetupPage } from './pages/FinancialSetup';
import {
  FinancialOverviewPage,
  MonthlySummaryPage,
  ReconciliationPage,
  ReportDrilldownPage,
} from './pages/FinancialReports';
import { BalanceSheetPage, CashFlowPage, ProfitAndLossPage } from './pages/FinancialStatements';
import { PartnersStatementPage, PartnerStatementPage } from './pages/PartnerStatements';
import {
  MoneyAccountFormPage,
  CutoverFormPage,
  OpeningBalanceFormPage,
  CutoverConfirmPage,
  CutoverReopenPage,
} from './pages/FinancialSetupForm';

export function App() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // Forgot password's two pages are open to anyone, signed in or not: the email's link lands on one.
  if (pathname === '/forgot-password') return <ForgotPasswordPage />;
  if (pathname === '/reset-password') return <ResetPasswordPage />;

  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <LoadingBlock />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />

        <Route path="/clients" element={<ClientsPage />} />
        {/* Form routes come before `:id` so "new" is never read as an id. */}
        <Route path="/clients/new" element={<ClientFormPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/clients/:id/edit" element={<ClientFormPage />} />
        <Route path="/clients/:id/contacts/new" element={<ClientContactFormPage />} />
        <Route path="/clients/:id/contacts/:contactId/edit" element={<ClientContactFormPage />} />

        {/* Projects are run in the admin; sys only reads what a project has billed and collected. */}
        <Route path="/projects/:id" element={<ProjectMoneyPage />} />

        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<InvoiceFormPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/invoices/:id/payments/new" element={<PaymentFormPage />} />
        <Route path="/invoices/:id/discount" element={<InvoiceDiscountPage />} />
        <Route path="/invoices/:id/cancel" element={<InvoiceCancelPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/payments/:paymentId/reverse" element={<PaymentReversePage />} />

        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/contracts/new" element={<ContractFormPage />} />
        <Route path="/contracts/:id" element={<ContractDetailPage />} />
        <Route path="/contracts/:id/edit" element={<ContractFormPage />} />
        <Route path="/contracts/:id/terminate" element={<ContractTerminatePage />} />

        {/* Subscriptions to the internal projects' packages (2026-10-28). */}
        <Route path="/subscription-overview" element={<SubscriptionsOverviewPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/new" element={<SubscriptionFormPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
        <Route path="/subscriptions/:id/edit" element={<SubscriptionTermsPage />} />
        <Route path="/subscriptions/:id/cancel" element={<SubscriptionCancelPage />} />
        <Route path="/subscription-packages" element={<PackagesPage />} />
        <Route path="/subscription-packages/new" element={<PackageFormPage />} />
        <Route path="/subscription-packages/offer" element={<OfferProjectPage />} />
        <Route path="/subscription-packages/:id" element={<PackageDetailPage />} />
        <Route path="/subscription-packages/:id/edit" element={<PackageFormPage />} />
        <Route path="/subscription-packages/:id/archive" element={<PackageArchivePage />} />
        <Route path="/subscription-billing" element={<SubscriptionBillingPage />} />

        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/expenses/new" element={<ExpenseFormPage />} />
        <Route path="/expenses/categories/new" element={<ExpenseCategoryFormPage />} />
        <Route path="/budgets" element={<BudgetsPage />} />
        <Route path="/budgets/new" element={<BudgetFormPage />} />

        <Route path="/payroll" element={<PayrollPage />} />
        <Route path="/payroll/new" element={<PayrollPeriodFormPage />} />
        <Route path="/payroll/:id" element={<PayrollPeriodPage />} />
        <Route path="/payroll/:id/entries/:entryId/adjust" element={<PayrollAdjustmentFormPage />} />
        {/* The cash employees redeemed wallet points for: paid with the salary, or here by hand (2026-10-20). */}
        <Route path="/points-rewards" element={<PointsRewardsPage />} />
        <Route path="/points-rewards/:id/mark-paid" element={<PointsRewardPaidPage />} />
        <Route path="/points-rewards/:id/cancel" element={<PointsRewardCancelPage />} />
        <Route path="/salaries" element={<SalariesPage />} />
        <Route path="/salaries/:userId" element={<SalaryHistoryPage />} />
        <Route path="/salaries/:userId/new" element={<SalaryFormPage />} />

        {/* Finance Phase 1 (2026-10-28): partners, the historical months, the opening position. */}
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/new" element={<PartnerFormPage />} />
        <Route path="/partners/:id" element={<PartnerDetailPage />} />
        <Route path="/partners/:id/edit" element={<PartnerFormPage />} />
        <Route path="/partners/:id/shares/new" element={<PartnerShareFormPage />} />
        <Route path="/partner-transactions" element={<PartnerTransactionsPage />} />
        <Route path="/partner-transactions/new" element={<PartnerTransactionFormPage />} />
        <Route path="/partner-transactions/:id" element={<PartnerTransactionPage />} />
        <Route path="/partner-transactions/:id/reverse" element={<PartnerTransactionReversePage />} />
        <Route path="/historical" element={<HistoricalFinancePage />} />
        <Route path="/historical/new" element={<HistoricalRecordFormPage />} />
        <Route path="/historical/:id" element={<HistoricalRecordPage />} />
        <Route path="/historical/:id/edit" element={<HistoricalRecordFormPage />} />
        <Route path="/historical/:id/finalize" element={<HistoricalFinalizePage />} />
        <Route path="/historical/:id/correct" element={<HistoricalCorrectPage />} />
        <Route path="/historical/:id/void" element={<HistoricalVoidPage />} />
        <Route path="/financial-setup" element={<FinancialSetupPage />} />
        <Route path="/financial-setup/cutover" element={<CutoverFormPage />} />
        <Route path="/financial-setup/accounts/new" element={<MoneyAccountFormPage />} />
        <Route path="/financial-setup/accounts/:id/edit" element={<MoneyAccountFormPage />} />
        <Route path="/financial-setup/opening/new" element={<OpeningBalanceFormPage />} />
        <Route path="/financial-setup/opening/:lineId/edit" element={<OpeningBalanceFormPage />} />
        <Route path="/financial-setup/confirm" element={<CutoverConfirmPage />} />
        <Route path="/financial-setup/reopen" element={<CutoverReopenPage />} />

        {/* Finance Phase 2 (2026-10-28): the financial reports — read-only. */}
        <Route path="/financial-reports" element={<FinancialOverviewPage />} />
        <Route path="/financial-reports/profit-and-loss" element={<ProfitAndLossPage />} />
        <Route path="/financial-reports/balance-sheet" element={<BalanceSheetPage />} />
        <Route path="/financial-reports/cash-flow" element={<CashFlowPage />} />
        <Route path="/financial-reports/partners" element={<PartnersStatementPage />} />
        <Route path="/financial-reports/partners/:id" element={<PartnerStatementPage />} />
        <Route path="/financial-reports/monthly" element={<MonthlySummaryPage />} />
        <Route path="/financial-reports/reconciliation" element={<ReconciliationPage />} />
        <Route path="/financial-reports/drilldown" element={<ReportDrilldownPage />} />

        <Route path="/finance/migration" element={<DataMigrationPage />} />
        <Route path="/finance/migration/invoices/:id/restore" element={<RestoreInvoicePage />} />
        <Route
          path="/finance/migration/invoices/:id/discard"
          element={<DiscardMigrationRowPage kind="invoice" />}
        />
        <Route path="/finance/migration/payments/:id/restore" element={<RestorePaymentPage />} />
        <Route
          path="/finance/migration/payments/:id/discard"
          element={<DiscardMigrationRowPage kind="payment" />}
        />

        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/audit-log" element={<AuditLogPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Settings › External systems — the Finance admin's (2026-10-28). */}
        <Route path="/settings/external-systems/new" element={<ExternalSystemFormPage />} />
        <Route path="/settings/external-systems/:id" element={<ExternalSystemDetailPage />} />
        <Route path="/settings/external-systems/:id/edit" element={<ExternalSystemFormPage />} />
        <Route path="/settings/external-systems/:id/suspend" element={<SystemSuspendPage />} />
        <Route path="/settings/external-systems/:id/webhook-secret" element={<WebhookSecretPage />} />
        <Route path="/settings/external-systems/:id/tokens/new" element={<TokenIssuePage />} />
        <Route path="/settings/external-systems/:id/tokens/:tokenId/rotate" element={<TokenRotatePage />} />
        <Route path="/settings/external-systems/:id/tokens/:tokenId/revoke" element={<TokenRevokePage />} />
        <Route path="/account/password" element={<ChangePasswordPage />} />

        <Route path="/access" element={<AccessPage />} />
        <Route path="/access/new" element={<AccessFormPage />} />
        <Route path="/access/:userId/edit" element={<AccessFormPage />} />
        <Route path="/access/:userId/revoke" element={<AccessRevokePage />} />

        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}
